import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { throttle } from '@theme/utilities';

/**
 * Notion Golf — mobile sticky add-to-cart bar.
 *
 * Deliberately does NOT own a product form. It proxies the real buy-box button, so
 * variant selection, quantity, cart events, error handling and the added-to-cart
 * animation all keep running through Horizon's existing flow. Price is mirrored from
 * the buy box for the same reason — one source of truth for sale/compare-at markup.
 *
 * The buy box re-renders on variant change, so the trigger is resolved live on every
 * read rather than cached.
 */
class NotionStickyAtcComponent extends Component {
  requiredRefs = ['button'];

  /** @type {(() => void) | undefined} */
  #onScroll;

  /** @type {(() => void) | undefined} */
  #onVariantUpdate;

  /**
   * The bar renders as a sibling of `.product-information` so that `position: fixed`
   * is always viewport-relative, so it cannot scope by ancestor. `.product-details` is
   * the PDP buy box and exists nowhere else — product cards use `.quick-add` — so a
   * document-level lookup is already specific enough.
   */
  get #scope() {
    return this.closest('.product-information') ?? document;
  }

  /** The real add-to-cart button in the buy box. Re-queried because it re-renders. */
  get #trigger() {
    return this.#scope.querySelector('.product-details add-to-cart-component button[name="add"]');
  }

  get #priceSource() {
    return this.#scope.querySelector('.product-details product-price');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#sync();

    this.#onScroll = throttle(() => this.#updateVisibility(), 100);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });

    this.#onVariantUpdate = () => this.#sync();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#onScroll) {
      window.removeEventListener('scroll', this.#onScroll);
      window.removeEventListener('resize', this.#onScroll);
    }
    if (this.#onVariantUpdate) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }
  }

  /** Re-sync after a Section Rendering refresh, not only on first connect. */
  updatedCallback() {
    super.updatedCallback?.();
    this.#sync();
  }

  /** Bound declaratively via `on:click="/addToCart"` in the markup. */
  addToCart() {
    this.#trigger?.click();
  }

  #sync() {
    const trigger = this.#trigger;
    const { button, price } = this.refs;

    if (button instanceof HTMLButtonElement) {
      button.disabled = trigger instanceof HTMLButtonElement ? trigger.disabled : true;
    }

    const source = this.#priceSource;
    if (price instanceof HTMLElement && source) price.innerHTML = source.innerHTML;
  }

  /**
   * Show the bar only once the buy-box button has scrolled off the top. On a product
   * page the button starts below the fold, and revealing the bar before the shopper
   * has even reached it would just cover the page with a second identical CTA.
   */
  #updateVisibility() {
    const trigger = this.#trigger;
    if (!trigger) return this.#setVisible(false);

    const { bottom } = trigger.getBoundingClientRect();
    this.#setVisible(bottom < 0);
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    if (this.dataset.visible === String(visible)) return;
    this.dataset.visible = String(visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
  }
}

if (!customElements.get('notion-sticky-atc-component')) {
  customElements.define('notion-sticky-atc-component', NotionStickyAtcComponent);
}
